// ============================================================
// v5.5 §3 — foreground audible + haptic alerts. The complement to
// v5 Web Push (which only reaches a *backgrounded* phone): a short
// beep + vibrate when the player in-hand must ACT or has new info.
//
// Everything here is best-effort and capability-guarded: no Web
// Audio / no Vibration / a denied AudioContext all degrade to a
// silent no-op (the app runs unchanged). The decision logic
// (`shouldAlert`) is pure and unit-tested; the audio/vibrate calls
// are thin and safe to import in Node (vitest).
//
// iOS reality (don't oversell): Web Audio only plays after a user
// gesture (we unlock the AudioContext on the first tap), and
// `navigator.vibrate` is unsupported on iOS Safari entirely — the
// buzz is an Android bonus, the beep is the iOS win.
// ============================================================
import type { PublicState, Role } from '../../shared/protocol';
import { WHISTLE_SYNTH } from '../../shared/constants';

/** v11 §2A adds the audio arc: `act-negative` (a curse lands on you — minor,
 *  descending), `relief` (it clears — major, rising), `round-active` (the
 *  seeker's "BOLT!" go-cue), `round-ended` (the round bookend, both sides). */
export type AlertIntensity = 'act' | 'ping' | 'act-negative' | 'relief' | 'round-active' | 'round-ended' | 'veto' | 'bank' | 'photo-demand';
/** A discrete alert to fire, with a stable `key` for replay de-dup (§3.2). */
export interface AlertEvent {
  key: string;
  intensity: AlertIntensity;
}

// ---------------- mute toggle (persisted, default = sound ON) ----------------
const MUTE_KEY = 'bolt:muteAlerts';
export function alertsMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false; // no storage (SSR / private mode) ⇒ unmuted
  }
}
export function setAlertsMuted(muted: boolean): void {
  try {
    if (muted) localStorage.setItem(MUTE_KEY, '1');
    else localStorage.removeItem(MUTE_KEY);
  } catch {
    /* private mode — toggle is in-memory only this session */
  }
}

// ---------------- Web Audio (guarded, gesture-unlocked) ----------------
type Ctor = typeof AudioContext;
let ctx: AudioContext | null = null;
function audioCtor(): Ctor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}
function audioCtx(): AudioContext | null {
  const C = audioCtor();
  if (!C) return null;
  try {
    if (!ctx) ctx = new C();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

/** Unlock/resume the AudioContext from a user gesture (iOS swallows the first
 *  beep otherwise). Safe to call repeatedly. Wired to the first tap (below) and
 *  callable explicitly (e.g. the sound toggle). */
export function unlockAudio(): void {
  audioCtx();
}

// One-shot: the very first tap anywhere unlocks audio for the session.
if (typeof window !== 'undefined') {
  const once = () => {
    unlockAudio();
    window.removeEventListener('pointerdown', once);
    window.removeEventListener('touchstart', once);
  };
  window.addEventListener('pointerdown', once, { passive: true });
  window.addEventListener('touchstart', once, { passive: true });
}

function tone(ac: AudioContext, freq: number, start: number, dur: number, peak: number): void {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = 'square';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain).connect(ac.destination);
  osc.start(start);
  osc.stop(start + dur + 0.03);
}

// ---------------- v16 BOLT-072 / v17 BOLT-075: the Airhorn ----------------
/**
 * v29 BOLT-174 — THE WHISTLE (was the Airhorn). Numbers come from
 * `WHISTLE_SYNTH`; `whistle-synth.test.ts` pins them equal to the iOS table.
 *
 * A warble tone in BURSTS: a 3 kHz carrier whose pitch swings ±5% at 5 Hz,
 * with two extra partials so the spectrum can be pointed at, cut into six
 * short bursts because onsets are what the ear localizes. See the constant's
 * comment for why the band moved out of 220-466 Hz - a phone speaker could
 * never radiate it, which is why the field heard nothing.
 */
export function playWhistle(): boolean {
  const ac = audioCtx();
  if (!ac) return false;
  const t0 = ac.currentTime + 0.02;
  const { centerHz, depthPercent, rateHz, fadeSec, burstSec, gapSec, bursts, partials } = WHISTLE_SYNTH;

  const master = ac.createGain();
  master.gain.setValueAtTime(0.0001, t0);
  master.connect(ac.destination);

  // ONE LFO for the whole blast, wired into every partial's frequency. It must
  // never reach the output - its only job is to nudge the carriers.
  const lfo = ac.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.setValueAtTime(rateHz, t0);

  const total = bursts * burstSec + (bursts - 1) * gapSec;
  for (const [ratio, g] of partials) {
    const hz = centerHz * ratio;
    const osc = ac.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(hz, t0);
    // ±depthPercent of THIS partial's own pitch, so the timbre warbles as one.
    const depth = ac.createGain();
    depth.gain.value = hz * (depthPercent / 100);
    lfo.connect(depth);
    depth.connect(osc.frequency);
    const gain = ac.createGain();
    gain.gain.value = g;
    osc.connect(gain).connect(master);
    osc.start(t0);
    osc.stop(t0 + total + 0.05);
  }
  lfo.start(t0);
  lfo.stop(t0 + total + 0.05);

  // The burst envelope, on the master: fade in, hold, fade out, silence, repeat.
  // The fades are what stop each burst opening with a broadband click.
  for (let i = 0; i < bursts; i++) {
    const on = t0 + i * (burstSec + gapSec);
    master.gain.setValueAtTime(0.0001, on);
    master.gain.linearRampToValueAtTime(1.0, on + fadeSec);
    master.gain.setValueAtTime(1.0, on + burstSec - fadeSec);
    master.gain.linearRampToValueAtTime(0.0001, on + burstSec);
  }
  return true;
}

/** The lock-anchor sound check (waste-proof #1, web leg): a bright two-note
 *  confirm tone at healthy volume — "did you hear that?" is the probe, since
 *  the web cannot read the device's media volume. */
export function playConfirmTone(): boolean {
  const ac = audioCtx();
  if (!ac) return false;
  const t = ac.currentTime + 0.02;
  tone(ac, 880, t, 0.14, 0.5);
  tone(ac, 1318, t + 0.16, 0.2, 0.5);
  return true;
}

function beep(intensity: AlertIntensity): void {
  const ac = audioCtx();
  if (!ac) return;
  const t = ac.currentTime;
  switch (intensity) {
    case 'act':
      // two rising tones — loud + distinct ("do something now")
      tone(ac, 880, t, 0.12, 0.22);
      tone(ac, 1175, t + 0.14, 0.16, 0.22);
      return;
    case 'act-negative':
      // descending minor third — "a curse just landed on you" (v11 §2A)
      tone(ac, 622, t, 0.16, 0.22);
      tone(ac, 523, t + 0.18, 0.24, 0.22);
      return;
    case 'veto':
      // v14 BOLT-042: the game-show buzzer — a low, harsh double "EHH-EHH".
      // Your question was REJECTED (the old rising 'act' read as success).
      tone(ac, 220, t, 0.18, 0.24);
      tone(ac, 110, t + 0.2, 0.3, 0.26);
      return;
    case 'bank':
      // v16 BOLT-066: the cha-ching — a bright coin-drop triple. Banking
      // time is the hider's best moment; it gets its own note.
      tone(ac, 1319, t, 0.06, 0.2);
      tone(ac, 1568, t + 0.07, 0.06, 0.2);
      tone(ac, 2093, t + 0.14, 0.18, 0.22);
      return;
    case 'photo-demand':
      // v16 BOLT-069: the shutter — two crisp clicks. "Camera, NOW" must
      // not sound like every other demand.
      tone(ac, 1800, t, 0.03, 0.26);
      tone(ac, 1200, t + 0.07, 0.03, 0.26);
      return;
    case 'relief':
      // rising major arpeggio — "the curse cleared" (softer than act)
      tone(ac, 523, t, 0.1, 0.12);
      tone(ac, 659, t + 0.11, 0.1, 0.12);
      tone(ac, 784, t + 0.22, 0.16, 0.12);
      return;
    case 'round-active':
      // the seeker's "BOLT!" go-cue — urgent rising triple, the loudest cue
      tone(ac, 784, t, 0.09, 0.24);
      tone(ac, 988, t + 0.1, 0.09, 0.24);
      tone(ac, 1319, t + 0.2, 0.22, 0.26);
      return;
    case 'round-ended':
      // neutral falling bookend — "the round is over" (both sides)
      tone(ac, 880, t, 0.14, 0.16);
      tone(ac, 659, t + 0.16, 0.2, 0.16);
      return;
    default:
      // one soft tone — "new info, no action"
      tone(ac, 660, t, 0.1, 0.08);
  }
}

function vibrate(intensity: AlertIntensity): void {
  try {
    const nav = typeof navigator !== 'undefined' ? (navigator as Navigator & { vibrate?: (p: number | number[]) => boolean }) : null;
    if (!nav || typeof nav.vibrate !== 'function') return;
    const pattern: Record<AlertIntensity, number | number[]> = {
      act: [0, 90, 50, 90],
      'act-negative': [0, 160, 60, 160],
      relief: [0, 40, 40, 40],
      'round-active': [0, 90, 40, 90, 40, 180],
      'round-ended': [0, 120],
      veto: [0, 220, 90, 220], // BOLT-042: the harsh double buzz
      bank: [0, 40, 30, 40, 30, 80], // BOLT-066: the coin triple
      'photo-demand': [0, 60, 40, 60], // BOLT-069: the shutter double
      ping: 30,
    };
    nav.vibrate(pattern[intensity]);
  } catch {
    /* unsupported (iOS Safari) — silent no-op */
  }
}

/** Fire one alert (beep + vibrate), unless muted. Both channels are guarded, so
 *  on a platform without Web Audio / Vibration this is a harmless no-op (§3.4). */
export function fireAlert(intensity: AlertIntensity): void {
  if (alertsMuted()) return;
  beep(intensity);
  vibrate(intensity);
}

// ---------------- pure transition logic (unit-tested, §3) ----------------
/**
 * Diff the previous snapshot against the next one and report the alerts a player
 * in `role` should hear. Fires off TRANSITIONS only (never retroactively on the
 * first snapshot), and each event carries a stable `key` so the caller can
 * de-dup across reconnects/state replays (§3.2). `act` = must act now; `ping` =
 * new info.
 */
export function shouldAlert(prev: PublicState | null, next: PublicState, role: Role | null): AlertEvent[] {
  if (!prev) return []; // baseline: adopt the current state without alerting
  const out: AlertEvent[] = [];
  const isHider = role === 'hider';
  const isSeeker = role === 'seeker';

  // ---- v11 §2A: the round arc (public phase transitions — leak-free) ----
  if (prev.phase !== next.phase) {
    // head start begins → audible on both sides
    if (next.phase === 'headstart') {
      out.push({ key: `round-start:${next.timer?.headStartEndsAt ?? next.series?.roundIndex}`, intensity: 'ping' });
    }
    // "BOLT!" — headstart→active (an early "I'm hidden" triggers this
    // immediately; the cue keys off the PUBLIC phase change, never the hider's
    // private action).
    //
    // v29 BOLT-173 (Jake): "whenever a hider ends the head start and says that
    // they're hidden, that should cause an audible sound effect or notification
    // on both the seeker and the hider's device." It was SEEKER-ONLY, which is
    // the whole gap - this is the moment the game actually begins, and the one
    // beat with no sound for half the room. The hider hears their own "I'm
    // hidden" land; the seeker hears the starting gun. Same beat, one cue, and
    // the existing key already makes it fire exactly once per round.
    if (prev.phase === 'headstart' && next.phase === 'active') {
      out.push({ key: `round-active:${next.timer?.roundStartedAt ?? next.series?.roundIndex}`, intensity: 'round-active' });
    }
    // round over → audible bookend on both sides
    if (next.phase === 'intermission' || next.phase === 'ended') {
      out.push({ key: `round-ended:${next.timer?.roundEndsAt ?? next.series?.roundIndex}`, intensity: 'round-ended' });
    }
  }

  if (isHider) {
    // a photo capture window just opened → "take the photo now"
    const had = new Set((prev.photoWindows ?? []).map((w) => w.cardId));
    for (const w of next.photoWindows ?? []) {
      // v16 BOLT-069: the shutter cue — a photo demand no longer shares the
      // generic 'act' with everything else.
      if (!had.has(w.cardId)) out.push({ key: `photo:${w.cardId}:${w.endsAt}`, intensity: 'photo-demand' });
    }
    // v16 BOLT-066: the cha-ching — the banked total grew (hider-only field).
    if ((next.bankedMin ?? 0) > (prev.bankedMin ?? 0)) {
      out.push({ key: `bank:${next.bankedMin}`, intensity: 'bank' });
    }
    // a question entered its veto window → "decide veto"
    const hadP = new Set((prev.pendingPlays ?? []).map((p) => p.id));
    for (const p of next.pendingPlays ?? []) {
      if (!hadP.has(p.id)) out.push({ key: `pending:${p.id}`, intensity: 'act' });
    }
  }

  if (isSeeker) {
    // a curse just landed (the seeker is now blocked) → negative tone (v11 §2A)
    const before = prev.activeCurse?.endsAt ?? null;
    const after = next.activeCurse?.endsAt ?? null;
    if (after != null && after !== before) out.push({ key: `curse:${after}`, intensity: 'act-negative' });
    // …and relief when it clears mid-round (round end clears it too, but the
    // round-ended bookend already covers that — don't double-fire).
    if (before != null && after == null && prev.phase === 'active' && next.phase === 'active') {
      out.push({ key: `curse-cleared:${before}`, intensity: 'relief' });
    }
    // a play was vetoed → "where did my question go?" is answered loudly. The
    // server frames the seeker's veto banner with the title VETOED (§4).
    // v14 BOLT-042: the cue is the REJECTION buzzer — the rising 'act' pair
    // read as success ("gives off the wrong vibe", Jake).
    const b = next.banner;
    if (b && b.id !== prev.banner?.id && b.title === 'VETOED') out.push({ key: `banner:${b.id}`, intensity: 'veto' });
    // a photo arrived (a clue) → soft ping, no action required
    const hadPh = new Set((prev.photos ?? []).map((p) => p.id));
    for (const ph of next.photos ?? []) {
      if (!hadPh.has(ph.id)) out.push({ key: `photoArrived:${ph.id}`, intensity: 'ping' });
    }
  }

  return out;
}
